/*
 * Steel Pony Farm — fulfillment selector (redesigned 2026-06-21; site-wide
 * 2026-07-01 — markup moved to sections/spf-fulfillment-global.liquid in the
 * FOOTER group so the sheet opens on ANY page).
 *
 * Drives three UI pieces: the CHIP + mobile sticky BAR (cart page only) and
 * the slide-over SHEET ("Screen 2"), which the cart drawer's CTA now opens IN
 * PLACE (no navigation). The sheet footer carries the CANDY RACK: 1–2 smart
 * complementary picks fetched fresh on every open from sections/spf-candy.liquid.
 *
 * Lifecycle-safe (Horizon morphs the cart section on every cart change):
 *   - State of record = CART ATTRIBUTES (survive to checkout + the order).
 *   - The UI is a projection: Liquid renders selected state from the data-*
 *     attributes, so a morph re-asserts state. JS re-hydrates on `cart:update`.
 *   - Click handling is delegated at the document level (survives morphs).
 *   - Sheet open/closed is pure UI state (not persisted).
 *
 * The $2 fee is NO LONGER a line item — it's a shipping rate, waived for
 * members by a native auto-discount. This script does NOT touch line items.
 *
 * Attribute keys (read by the SPF Ops pipeline):
 *   "Fulfillment Method" = "Pickup" | "Home Delivery" | "Mail" (seed-garlic-only carts)
 *   "Pickup Location"    = location name (pickup only)
 *   "Pickup Day"         = "Wednesday" | "Thursday" (pickup only)
 */
(function () {
  'use strict';

  const ATTR_METHOD = 'Fulfillment Method';
  const ATTR_LOCATION = 'Pickup Location';
  const ATTR_DAY = 'Pickup Day';
  const ATTR_TIME = 'Pickup Time';   // location window, e.g. "3:45–5:30 PM"
  const ATTR_DATE = 'Pickup Date';   // computed, e.g. "Thursday, June 25"
  const PICKUP = 'Pickup';
  const DELIVERY = 'Home Delivery';
  const MAIL = 'Mail';
  const REMEMBER_KEY = 'spf_last_pickup'; // {name, day}
  const POSTAL_KEY = 'spf_delivery_postal'; // raw text of the last postal check
  const KEEP_SHOPPING_URL = '/'; // where the "keep shopping" CTA sends shoppers
  // Seed garlic mail (Mike, 2026-09-16): a gardener who arrived on the seed-garlic link is not
  // shopping for vegetables, and anything else they add takes the mail option away. On a
  // seed-garlic-only cart every "keep shopping" route leads back to seed garlic instead of home.
  const SEED_COLLECTION_URL = '/collections/seed-garlic';
  const keepShoppingUrl = () => (mailValid() ? SEED_COLLECTION_URL : KEEP_SHOPPING_URL);
  // Shown next to checkout while Shop Pay is on. Some Shop accounts are in a bad
  // state and their sign-in dies on a bare "Something went wrong" with no way out
  // (ticket 68883890, unresolved). "Continue with email" never failed, so name it
  // BEFORE they hit the fork — checkout is Shopify's page, the cart is ours.
  // Set to '' to remove once Shopify fixes the sign-in.
  const SHOP_SIGNIN_HINT =
    'Signing in with Shop? If it gets stuck, choose <strong>continue with email</strong> — that always works.';
  // Stale attribute keys left by the old CR app — cleared on any write.
  const CLEAR_CR = { delivery: '', Pickup_Store: '', Pickup_date: '', Delivery_date: '' };
  const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  const SHORT_DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const SHORT_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  const root = () => document.querySelector('[data-spf-fulfillment]');
  const money = (c) => '$' + (c / 100).toFixed(2);
  // The section renders site-wide (footer group); chip/bar/adoption stay
  // scoped to the cart page via this check.
  const onCartPage = () => !!document.querySelector('.cart-page');

  // ── Tuesday Rescue flash state (task-flash-clearance.md §3e) ──────────────
  // /cart.js has NO product tags, so cart-summary.liquid computes per-line flash
  // flags server-side and re-renders them on every Horizon cart morph; this just
  // reads the freshest copy from the DOM. allFlash → no minimum, no
  // pickup/delivery choice (the rescue rides the existing order; BeSure applies
  // the same exemption at checkout). mixed → checkout blocks with a split prompt.
  function flashState() {
    const s = document.querySelector('[data-spf-flash-state]');
    if (!s) return { flash: 0, allFlash: false, mixed: false, flashVariants: [], regVariants: [] };
    const list = (v) => String(v || '').split(',').filter(Boolean);
    return {
      flash: parseInt(s.dataset.flash || '0', 10) || 0,
      allFlash: s.dataset.allFlash === 'true',
      mixed: s.dataset.mixed === 'true',
      flashVariants: list(s.dataset.flashVariants),
      regVariants: list(s.dataset.regVariants),
    };
  }

  // ── Seed garlic MAIL (2026-09-15, task-seed-garlic-storefront.md) ─────────
  // Same server-rendered span as flash (cart-summary.liquid). Mail is valid ONLY while every
  // line is seed garlic: only that delivery profile carries a mail rate, and checkout offers
  // just the methods every line shares, so one bunch of carrots removes mail entirely.
  // Fails CLOSED: with no span on the page, mail is not a valid choice, so the gate asks the
  // shopper to choose again instead of sending a "Mail" cart to a checkout with no mail rate
  // (Shipzy hides pickup and delivery for it — zero methods, uncompletable).
  function mailValid() {
    const s = document.querySelector('[data-spf-flash-state]');
    return !!s && s.dataset.allSeed === 'true' && s.dataset.allFlash !== 'true';
  }
  function cartHasSeed() {
    const s = document.querySelector('[data-spf-flash-state]');
    return !!s && (parseInt(s.dataset.seed || '0', 10) || 0) > 0;
  }

  function closeCartDrawer() {
    const d = document.querySelector('cart-drawer-component');
    if (d && typeof d.close === 'function') { try { d.close(); return; } catch (e) {} }
    const dlg = d && d.querySelector('dialog');
    if (dlg && dlg.open) { try { dlg.close(); } catch (e) {} }
  }

  // ── Deterministic fulfillment date (cutoff Mon 11:59pm) ───────────────────
  function fulfillmentTarget(day) {          // day: 3=Wed, 4=Thu → Date at midnight
    const now = new Date();
    const dow = now.getDay();
    const daysUntilMonday = (1 - dow + 7) % 7;
    const target = new Date(now);
    target.setHours(0, 0, 0, 0);
    target.setDate(now.getDate() + daysUntilMonday + (day - 1));
    return target;
  }
  function fulfillmentDate(day) {             // e.g. "Thursday, June 25"
    const t = fulfillmentTarget(day);
    return `${DAYS[t.getDay()]}, ${MONTHS[t.getMonth()]} ${t.getDate()}`;
  }
  function shortDate(day) {                   // e.g. "Thu, Jun 25" — for tight delivery copy
    const t = fulfillmentTarget(day);
    return `${SHORT_DAYS[t.getDay()]}, ${SHORT_MONTHS[t.getMonth()]} ${t.getDate()}`;
  }
  // A Tue-Thu order is for NEXT week's Wed/Thu. A shopper ordering on a Thursday read
  // "Thursday" as today and cancelled #2361 (2026-10-01), so the shopper-facing copy says
  // "next week" on a Tue-Thu order, when this week's run is already under way. Display only:
  // the Pickup Date attribute (and the confirmation email built from it) keeps the plain date.
  function isNextWeek() {
    const dow = new Date().getDay();
    return dow >= 2 && dow <= 4;
  }
  function shownDate(day) {                   // e.g. "Thursday, October 8 (next week)"
    return fulfillmentDate(day) + (isNextWeek() ? ' (next week)' : '');
  }

  const getCart = () =>
    fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());

  function writeAttributes(attrs) {
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes: attrs }),
    });
  }

  function hasChoice(el) {
    if (el.dataset.method === MAIL) return mailValid();
    return el.dataset.method === DELIVERY || !!el.dataset.location;
  }

  // SPF: NO phone handling here (removed 2026-07-01 — it double-asked). Native
  // checkout requires phone on the address for every order (pickup is a
  // shipping rate, so pickup orders fill the address form too), and SMS/ops
  // read shippingAddress.phone — the old "Phone" cart attribute had no readers.

  // ── Postal eligibility check (delivery panel) ─────────────────────────────
  // Checkout is the wrong place to learn you're ineligible: with zero methods
  // Shopify blames the ADDRESS ("cannot be shipped to the selected address")
  // even when the real problem is the $50 minimum, and that copy isn't
  // editable on this plan. So the sheet settles eligibility up front, from the
  // same zone list as admin (section setting data-postal-zones — 3-char
  // entries match as FSA prefixes, 6-char entries match exactly; the T4E
  // exact codes are the Gasoline Alley pockets).
  function normPostal(v) {
    return String(v || '').toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 6);
  }
  function postalLists(el) {
    const parse = (s) =>
      String(s || '').toUpperCase().replace(/[^A-Z0-9,]/g, '').split(',').filter(Boolean);
    return { zones: parse(el.dataset.postalZones), north: parse(el.dataset.postalNorth) };
  }
  // → 'empty' | 'typing' | 'in-north' | 'in-south' | 'partial' (FSA needs the
  //   full code, e.g. T4E) | 'out'
  function classifyPostal(el, raw) {
    const code = normPostal(raw);
    if (!code) return { state: 'empty', code };
    if (code.length < 3) return { state: 'typing', code };
    const lists = postalLists(el);
    const prefixes = lists.zones.filter((z) => z.length === 3);
    const exacts = lists.zones.filter((z) => z.length === 6);
    const fsa = code.slice(0, 3);
    if (prefixes.indexOf(fsa) !== -1) {
      return { state: lists.north.indexOf(fsa) !== -1 ? 'in-north' : 'in-south', code, fsa };
    }
    if (exacts.some((x) => x.slice(0, 3) === fsa)) {
      if (code.length < 6) return { state: 'partial', code, fsa };
      if (exacts.indexOf(code) !== -1) {
        return { state: lists.north.indexOf(code) !== -1 ? 'in-north' : 'in-south', code, fsa };
      }
      return { state: 'out', code, fsa };
    }
    return { state: 'out', code, fsa };
  }
  // Paint the status line under the input; returns the state so renderGate can
  // fold it into the continue-button logic. Also stamps el.dataset.postalState
  // (the cart-page checkout gate reads it without re-classifying).
  function renderPostal(el) {
    const input = el.querySelector('[data-spf-postal]');
    const status = el.querySelector('[data-spf-postal-status]');
    if (!input || !status) { el.dataset.postalState = 'empty'; return 'empty'; }
    const r = classifyPostal(el, input.value);
    el.dataset.postalState = r.state;
    let tone = 'hint';
    let html = '';
    if (r.state === 'in-north') {
      tone = 'ok';
      html = '✓ We deliver to you — <strong>Thursdays</strong> (next: ' + shortDate(4) + ')';
    } else if (r.state === 'in-south') {
      tone = 'ok';
      html = '✓ We deliver to you — <strong>Wednesdays</strong> (next: ' + shortDate(3) + ')';
    } else if (r.state === 'partial') {
      html = r.fsa + ' delivery covers Gasoline Alley only — enter your full postal code to check.';
    } else if (r.state === 'out') {
      tone = 'warn';
      html = 'We don’t deliver to <strong>' + r.code + '</strong> yet — but 10 pickup spots have you covered.';
    } else {
      html = 'We deliver to Red Deer (T4N, T4P, T4R) and Gasoline Alley.';
    }
    status.dataset.spfTone = tone;
    status.innerHTML = html; // safe: code/fsa are normPostal'd to [A-Z0-9]
    return r.state;
  }
  function hydratePostal(el) {
    const input = el.querySelector('[data-spf-postal]');
    if (input && !input.value) {
      try { const v = localStorage.getItem(POSTAL_KEY); if (v) input.value = v; } catch (e) {}
    }
    renderPostal(el);
  }

  // ── Remember last pickup spot (1-tap repeat) ──────────────────────────────
  function rememberPickup(name, day, hours) {
    try { localStorage.setItem(REMEMBER_KEY, JSON.stringify({ name, day, hours })); } catch (e) {}
  }
  function recallPickup() {
    try { return JSON.parse(localStorage.getItem(REMEMBER_KEY) || 'null'); } catch (e) { return null; }
  }

  // ── Sheet open / close ────────────────────────────────────────────────────
  // Opening is the moment of truth site-wide: adopt the remembered spot, paint
  // selection state, refresh the $50 gate from the live cart, and fetch the
  // candy-rack picks (the sheet markup itself is static from page load).
  function openSheet() {
    const el = root();
    if (!el) return;
    const sheet = el.querySelector('[data-spf-sheet]');
    if (!sheet) return;
    adoptRemembered(el);
    hydratePostal(el);
    renderSelection(el);
    renderChip(el);
    getCart().then((c) => renderGate(c, el));
    loadCandy();
    sheet.hidden = false;
    // force reflow so the open transition runs
    void sheet.offsetWidth;
    sheet.classList.add('is-open');
    document.documentElement.style.overflow = 'hidden';
    // Repeat buyer with a remembered spot: bring the SELECTED location into view so
    // the sheet doesn't open on a dimmed list with the choice + confirm pill below
    // the fold (On-Farm is #10 of 10) — which reads as "why is everything greyed out?"
    requestAnimationFrame(() => {
      scrollToSelectedLocation(el);
      // Delivery chosen but no postal code yet: open on the postal box.
      if (el.dataset.method === DELIVERY) {
        const ps = el.dataset.postalState;
        if (ps !== 'in-north' && ps !== 'in-south' && ps !== 'out') focusPostal(el);
      }
    });
  }
  // Center the chosen pickup row within the sheet's scroll area. No-op for delivery
  // or when nothing's chosen (a fresh visitor should land on the cutoff + toggle at
  // the top). Uses rect deltas (transform-invariant, so it's safe mid-open-animation)
  // and only moves the inner scroller — never the locked document.
  function scrollToSelectedLocation(el) {
    if (!el || el.dataset.method === DELIVERY || !el.dataset.location) return;
    const scroller = el.querySelector('.spf-sheet__scroll');
    const selected = el.querySelector('[data-spf-loc].is-selected');
    if (!scroller || !selected) return;
    const sRect = scroller.getBoundingClientRect();
    const tRect = selected.getBoundingClientRect();
    const delta = (tRect.top - sRect.top) - (scroller.clientHeight / 2 - selected.clientHeight / 2);
    scroller.scrollTop += delta;
  }
  // Bring the postal box into view inside the sheet and focus it. On a short
  // screen (a laptop in the Facebook browser, 2026-10-23) the delivery panel sat
  // behind the foot and the customer could not see the box the button named.
  function focusPostal(el) {
    if (!el) return;
    const scroller = el.querySelector('.spf-sheet__scroll');
    const input = el.querySelector('[data-spf-postal]');
    if (!input) return;
    if (scroller) {
      const sRect = scroller.getBoundingClientRect();
      const tRect = input.getBoundingClientRect();
      scroller.scrollTop += (tRect.top - sRect.top) - (scroller.clientHeight / 2 - input.clientHeight / 2);
    }
    try { input.focus({ preventScroll: true }); } catch (_) { input.focus(); }
  }
  function closeSheet() {
    const el = root();
    if (!el) return;
    const sheet = el.querySelector('[data-spf-sheet]');
    if (!sheet) return;
    sheet.classList.remove('is-open');
    document.documentElement.style.overflow = '';
    const done = () => { sheet.hidden = true; sheet.removeEventListener('transitionend', done); };
    sheet.addEventListener('transitionend', done);
    setTimeout(done, 360); // fallback if transitionend doesn't fire
  }

  // Second-order eligibility has TWO doors (Lisa, launch morning 2026-08-24): the
  // customer tag (signed-in, server-rendered) OR the ROUND2 discount cookie set by
  // the /discount link — because SMS recipients are often signed out in the browser
  // that opens the link, and the soft gate was blocking exactly the people the
  // message invited. The cookie only opens the SOFT gate; checkout (BeSure) still
  // verifies the real tag by email, so a shared link can't buy anything sub-$50.
  function hasRound2Cookie() {
    const m = document.cookie.match(/(?:^|;\s*)discount_code=([^;]*)/);
    return !!m && /round2/i.test(decodeURIComponent(m[1] || ''));
  }
  function secondOrderEligible(el) {
    return (el && el.dataset.spfSecondorder === '1') || hasRound2Cookie();
  }

  // Fundraiser exemption (task-fundraiser-program.md §5c). A fundraiser cart carries a $0
  // marker product that the checkout rule exempts from the $50 minimum by tag. This gate
  // has to agree with it: if it did not, the shopper would be blocked by OUR button while
  // checkout would have let them through — a wall with no explanation, for exactly the
  // first-time buyer the whole program is trying to keep. Matched on handle because
  // /cart.js carries handles but not tags (the same limitation the server-rendered
  // flash-state span exists to work around). Blank setting → always false.
  function fundraiserCart(cart, el) {
    var handle = el && el.dataset.spfFundraiserMarker;
    if (!handle) return false;
    var items = (cart && cart.items) || [];
    for (var i = 0; i < items.length; i++) if (items[i].handle === handle) return true;
    return false;
  }

  // Second-order routing applies ONLY to a regular cart: flash/rescue carts have
  // their own exemption and must never carry ROUND2 (free shipping they don't
  // need, and the order would mis-fold as an add-on) — adversarial audit C4.
  function secondOrderRoute(el) {
    if (!secondOrderEligible(el)) return false;
    const fs = flashState();
    return !fs.allFlash && !fs.mixed;
  }

  function triggerCheckout() {
    // Second-order customers route through Shopify's discount URL so ROUND2 is
    // applied before checkout — BeSure's $50 exemption keys on the code being
    // present, and the code is what zeroes the packing/delivery fee.
    const el = root();
    if (secondOrderRoute(el)) {
      window.location.href = '/discount/ROUND2?redirect=%2Fcheckout';
      return;
    }
    const btn = document.getElementById('checkout');
    if (btn && !btn.disabled) { btn.click(); return; }
    window.location.href = '/checkout';
  }

  // SPF H1: re-stamp the pickup date for the currently-selected spot from "now", so a
  // cart that sat across the Monday cutoff can't carry last week's date onto the order.
  // No-op for delivery or when nothing's chosen. Uses the same compute-and-save a
  // location tap already trusts; returns a promise so callers can checkout after.
  //
  // 2026-09-09 — IT NOW RE-ASSERTS THE WHOLE SET, NOT JUST THE DATE.
  //
  // It used to write only Day/Time/Date. `/cart/update.js` MERGES attributes, but an app
  // writing through the Storefront API's cartAttributesUpdate REPLACES the whole set — so a
  // third-party cart write silently drops "Fulfillment Method" and "Pickup Location", and
  // this function then restored three of the five. The customer lands in checkout with a
  // pickup DAY and TIME but no METHOD and no LOCATION.
  //
  // That is not cosmetic: with no fulfillment method Shipzy offers no pickup rate, so the
  // checkout has ZERO shipping methods and CANNOT be completed — the exact trap already
  // documented in sync() below. The customer believes they ordered and no order exists.
  //
  // Proven live: abandoned checkout #38203562754092 (2026-09-08, $117.55) carries
  // discount_codes + removed_freebies — two attributes this theme never writes, so an app
  // replaced the set — plus Pickup Day/Time/Date and NO method or location. The customer
  // emailed the next day asking where her groceries were.
  //
  // Re-asserting all five is idempotent and self-healing: whatever wiped them, the checkout
  // click puts them back. Same for delivery, which had the identical hole.
  function refreshPickupDate(el) {
    if (!el) return Promise.resolve();
    // A rescue cart must carry NO fulfillment attributes (see sync()) — never re-stamp one.
    if (flashState().allFlash) return Promise.resolve();
    // Mail clears the pickup set too: a stale Pickup Location left beside "Mail" would put the
    // order on a pickup sheet. An invalid mail choice writes nothing — the gate blocks it.
    if (el.dataset.method === MAIL) {
      if (!mailValid()) return Promise.resolve();
      return writeAttributes({
        [ATTR_METHOD]: MAIL, [ATTR_LOCATION]: '', [ATTR_DAY]: '', [ATTR_TIME]: '', [ATTR_DATE]: '',
      });
    }
    if (el.dataset.method === DELIVERY) {
      return writeAttributes({ [ATTR_METHOD]: DELIVERY });
    }
    if (!el.dataset.location) return Promise.resolve();
    const day = parseInt(el.dataset.locationDay || '3', 10);
    return writeAttributes({
      [ATTR_METHOD]: PICKUP,
      [ATTR_LOCATION]: el.dataset.location,
      [ATTR_DAY]: String(el.dataset.locationDay) === '4' ? 'Thursday' : 'Wednesday',
      [ATTR_TIME]: el.dataset.locationHours || '',
      [ATTR_DATE]: fulfillmentDate(day),
    });
  }

  // ── Candy rack: 1–2 smart picks, fetched fresh on every sheet open ───��────
  // Server-rendered by sections/spf-candy.liquid (Section Rendering API) so the
  // picks always reflect the LIVE cart. No genuine pick → block stays hidden.
  // ── Suggestion telemetry (fire-and-forget → box /api/recs-beacon) ─────────
  // Counts impressions and adds per suggestion surface so the Friday digest can
  // say whether the suggestions are seen/used. No customer identity, no cart
  // contents. Shared via window.SPFBeacon for the other suggestion scripts.
  const SPF_BEACON_URL = 'https://app.steelpony.ca/api/recs-beacon';
  function spfBeacon(surface, event, variantId, count) {
    try {
      const body = JSON.stringify({ s: surface, e: event, v: variantId || null, n: count || 1 });
      if (navigator.sendBeacon) navigator.sendBeacon(SPF_BEACON_URL, body);
      else fetch(SPF_BEACON_URL, { method: 'POST', body, keepalive: true, headers: { 'Content-Type': 'text/plain' } }).catch(() => {});
    } catch (e) { /* never break shopping over telemetry */ }
  }
  window.SPFBeacon = spfBeacon;

  // cart-page smart-recs grid impression (once per page view; grid is server-rendered)
  function beaconCartpageImp() {
    if (!/\/cart\b/.test(location.pathname)) return;
    const cards = document.querySelectorAll('.spf-recs [data-spf-candy-item]').length;
    if (cards > 0) spfBeacon('cartpage', 'imp', null, cards);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', beaconCartpageImp);
  else beaconCartpageImp();

  let candyLoaded = false;
  function loadCandy() {
    const el = root();
    const box = el && el.querySelector('[data-spf-candy]');
    if (!box) return;
    const rootUrl = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    fetch(rootUrl + '?section_id=spf-candy', { headers: { Accept: 'text/html' } })
      .then((r) => { if (!r.ok) throw new Error('candy ' + r.status); return r.text(); })
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const rows = doc.querySelector('[data-spf-candy-rows]');
        const slot = box.querySelector('[data-spf-candy-slot]');
        const count = rows ? rows.querySelectorAll('[data-spf-candy-item]').length : 0;
        candyLoaded = count > 0;
        if (slot) slot.innerHTML = candyLoaded ? rows.innerHTML : '';
        // No add-on picks on a mail cart: every pick is produce, and one tap would take mail away.
        box.hidden = !(candyLoaded && hasChoice(el) && el.dataset.method !== MAIL);
        if (!box.hidden) spfBeacon('sheet', 'imp', null, count);
      })
      .catch(() => { candyLoaded = false; box.hidden = true; });
  }
  function bumpCartIcon(d) {
    document.querySelectorAll('cart-icon').forEach((i) => {
      if (typeof i.beginOptimistic === 'function') i.beginOptimistic(d);
    });
  }
  // SPF fundraiser marker (task-fundraiser-program.md §5c): the $0 line is hidden in the
  // cart, so it must not be counted in the header badge either. The Liquid path already
  // excludes it (cart-bubble.liquid, and the ref="cartItemCount" span the cart component
  // reads); this broadcast is the one place a RAW /cart.js count reaches cart-icon.js, so
  // it has to agree or the badge reads one higher until the next page load. Handle, not
  // tag — /cart.js has no tags. Blank setting → unchanged count.
  function visibleItemCount(cart) {
    var el = document.querySelector('[data-spf-fundraiser-marker]');
    var handle = el && el.getAttribute('data-spf-fundraiser-marker');
    handle = handle ? handle.trim() : '';
    var n = (cart && cart.item_count) || 0;
    if (!handle) return n;
    var items = (cart && cart.items) || [];
    for (var i = 0; i < items.length; i++) {
      if (items[i].handle === handle) n -= items[i].quantity;
    }
    return n < 0 ? 0 : n;
  }

  // Mirror spf-cart-nudge.js's post-add broadcast so the header bubble, the
  // drawer, and this block all re-sync from one cart payload.
  function broadcastCart(cart) {
    var visible = visibleItemCount(cart);
    ['cart:update', 'cart:refresh', 'cart:updated', 'theme:cart:update'].forEach((n) => {
      document.dispatchEvent(new CustomEvent(n, {
        detail: { resource: cart, cart: cart, data: { itemCount: visible } },
        bubbles: true,
      }));
    });
  }

  // ── Render: confirmation pill (date for pickup / breakdown for delivery) ──
  function renderConfirm(el) {
    const confirm = el.querySelector('[data-spf-confirm]');
    if (!confirm) return;
    const title = confirm.querySelector('[data-spf-confirm-title]');
    const sub = confirm.querySelector('[data-spf-confirm-sub]');
    const isMember = el.dataset.member === 'true';
    const pack = parseFloat(el.dataset.packingFee || '2');
    const del = parseFloat(el.dataset.deliveryFee || '7');

    if (el.dataset.method === MAIL && mailValid()) {
      if (title) title.textContent = 'Mailed to you — tracked parcel';
      if (sub) sub.textContent = 'Anywhere in Canada · shipping by weight, shown at checkout';
      confirm.classList.add('show');
    } else if (el.dataset.method === DELIVERY) {
      // With a checked postal code we can COMMIT to the route day; otherwise
      // show both upcoming dates so "Wed or Thu" can't read as next-day when
      // it's really this coming week.
      const ps = el.dataset.postalState;
      // OUT OF ZONE: say nothing here. The panel already carries the refusal in red
      // ("We don't deliver to T4E1N6 yet") and a Choose Local Pickup button, and a green
      // ticked pill quoting "$2 packing + $7 delivery = $9" directly underneath it
      // contradicts both — it reads as a delivery that has been accepted and priced.
      // Reported from the live store 2026-09-15 on the seed garlic page, where an
      // out-of-area postal code is the NORMAL case rather than a mistake: every
      // mail-order gardener lands here.
      if (ps === 'out') { confirm.classList.remove('show'); return; }
      const dayBit =
        ps === 'in-north' ? { t: `Home Delivery — ${shortDate(4)}`, s: 'Thursday route (north)' }
        : ps === 'in-south' ? { t: `Home Delivery — ${shortDate(3)}`, s: 'Wednesday route (south)' }
        : { t: `Home Delivery — ${shortDate(3)} or ${shortDate(4)}`, s: "we'll confirm your day" };
      if (title) title.textContent = dayBit.t;
      if (sub) {
        sub.textContent = isMember
          ? `$${del} delivery · packing waived (member) · ${dayBit.s}`
          : `$${pack} packing + $${del} delivery = $${pack + del} · ${dayBit.s}`;
      }
      confirm.classList.add('show');
    } else if (el.dataset.location) {
      const day = parseInt(el.dataset.locationDay || '3', 10);
      const hrs = el.dataset.locationHours || '';
      if (title) title.textContent = 'Pickup ' + shownDate(day);
      if (sub) {
        const feeBit = isMember ? 'packing waived (member)' : `+$${pack} packing fee`;
        sub.textContent = hrs ? `${hrs} · ${feeBit}` : feeBit;
      }
      confirm.classList.add('show');
    } else {
      confirm.classList.remove('show');
    }
  }

  // ── Render: method/location selection state in the sheet ──────────────────
  function renderSelection(el) {
    const canMail = mailValid();
    // A mail choice on a cart that can no longer be mailed shows as the default pickup panel.
    let method = el.dataset.method || PICKUP;
    if (method === MAIL && !canMail) method = PICKUP;
    el.querySelectorAll('[data-spf-method]').forEach((b) =>
      b.setAttribute('aria-selected', b.dataset.spfMethod === method)
    );
    const toggle = el.querySelector('.toggle');
    if (toggle) {
      toggle.classList.toggle('is-delivery', method === DELIVERY);
      toggle.classList.toggle('is-mail', method === MAIL);
      toggle.classList.toggle('has-mail', canMail);
    }
    const mailTab = el.querySelector('[data-spf-mail-tab]');
    if (mailTab) mailTab.hidden = !canMail;
    // Seed garlic sharing a cart with anything else: say why mail isn't offered.
    const mailNote = el.querySelector('[data-spf-mail-note]');
    if (mailNote) mailNote.hidden = canMail || !cartHasSeed();
    const pickup = el.querySelector('#panel-pickup');
    const delivery = el.querySelector('#panel-delivery');
    const mail = el.querySelector('#panel-mail');
    if (pickup) pickup.classList.toggle('show', method === PICKUP);
    if (delivery) delivery.classList.toggle('show', method === DELIVERY);
    if (mail) mail.classList.toggle('show', method === MAIL);

    const locName = el.dataset.location || '';
    const list = el.querySelector('[data-spf-list]');
    if (list) {
      list.classList.toggle('has-selection', !!locName);
      list.querySelectorAll('[data-spf-loc]').forEach((b) => {
        b.classList.toggle('is-selected', b.dataset.spfLoc === locName);
        const tag = b.querySelector('.day');
        if (tag) tag.textContent = shortDate(parseInt(b.dataset.spfDay || '3', 10));
      });
      const head = pickup && pickup.querySelector('.panel-head .muted');
      if (head) {
        head.textContent = `Pick a location. Orders placed now are picked up ${shortDate(3)} or ${shortDate(4)}` +
          (isNextWeek() ? ' (next week).' : '.');
      }
    }
    renderConfirm(el);
    // Candy rack shows only once a fulfillment choice exists (and picks loaded).
    const candy = el.querySelector('[data-spf-candy]');
    if (candy) candy.hidden = !(candyLoaded && hasChoice(el) && method !== MAIL);
  }

  // ── Render: the cart chip ─────────────────────────────────────────────────
  // Relocate the chip to the very top of the cart (between the title and the
  // first item) so the fulfillment choice is the first thing shoppers see.
  // Morph-safe: dedupe any chip Horizon's section re-render recreates, then
  // reposition the survivor before the items.
  function placeChip() {
    const items = document.querySelector('.cart-page__items');
    const chips = document.querySelectorAll('[data-spf-chip]');
    if (!items || !chips.length) return;
    const chip = chips[0];
    for (let i = 1; i < chips.length; i++) chips[i].remove();
    if (items.previousElementSibling !== chip) items.parentNode.insertBefore(chip, items);
  }

  function renderChip(el) {
    const chip = document.querySelector('[data-spf-chip]');
    if (!chip) return;
    // Rescue-only carts need no pickup/delivery choice — hide the chip entirely
    // rather than showing "Required before checkout" (which would be a lie).
    chip.hidden = flashState().allFlash;
    if (chip.hidden) return;
    const title = chip.querySelector('[data-spf-chip-title]');
    const sub = chip.querySelector('[data-spf-chip-sub]');
    const icon = chip.querySelector('[data-spf-chip-icon]');
    const action = chip.querySelector('.spf-chip__action');
    const method = el.dataset.method;

    chip.classList.toggle('spf-chip--empty', !hasChoice(el));
    if (action) action.textContent = hasChoice(el) ? 'Change' : 'Choose';

    // SPF: when a choice is locked in, show a persistent green "✓" on the chip
    // title so the done-state survives after the slide-over closes (the "Change"
    // action label alone didn't read as "this is confirmed").
    function setTitle(text, confirmed) {
      if (!title) return;
      title.textContent = '';
      if (confirmed) {
        const chk = document.createElement('span');
        chk.setAttribute('aria-hidden', 'true');
        chk.textContent = '✓ ';
        chk.style.color = '#2e7d32';
        chk.style.fontWeight = '700';
        title.appendChild(chk);
      }
      title.appendChild(document.createTextNode(text));
    }

    if (method === MAIL && mailValid()) {
      setTitle('Mail it to me', true);
      if (sub) sub.textContent = 'Tracked parcel · shipping shown at checkout';
      if (icon) icon.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M21 8l-9-5-9 5v8l9 5 9-5zM3 8l9 5 9-5M12 13v8"/></svg>';
    } else if (method === DELIVERY) {
      setTitle('Home Delivery', true);
      // Name the coming week's dates (not just "we'll confirm your day") so the
      // shopper knows the timing at a glance from the cart.
      if (sub) sub.textContent = `Red Deer · ${shortDate(3)} or ${shortDate(4)}` + (isNextWeek() ? ' (next week)' : '');
      if (icon) icon.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M1 3h13v11H1zM14 7h4l3 3v4h-7M3.5 17a2 2 0 104 0 2 2 0 00-4 0zM15.5 17a2 2 0 104 0 2 2 0 00-4 0z"/></svg>';
    } else if (el.dataset.location) {
      const day = parseInt(el.dataset.locationDay || '3', 10);
      const hrs = el.dataset.locationHours || '';
      setTitle('Pickup · ' + el.dataset.location, true);
      if (sub) sub.textContent = hrs ? `${shownDate(day)} · ${hrs}` : shownDate(day);
      if (icon) icon.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M3 9l1-5h16l1 5M4 9h16v10a1 1 0 01-1 1H5a1 1 0 01-1-1V9zM9 13h6"/></svg>';
    } else {
      setTitle('Choose pickup or delivery', false);
      if (sub) sub.textContent = 'Required before checkout';
    }
  }

  // ── Render: subtotal-driven bits (min bar, gate, sheet continue, bar) ─────
  function renderGate(cart, el) {
    const minimum = parseInt(el.dataset.minimum || '5000', 10); // cents
    const subtotal = cart.items_subtotal_price; // products only; fee is a shipping rate
    const fs = flashState();
    const rescue = fs.allFlash && cart.item_count > 0;
    // Second-order exemption (registry row 55): tagged this-cycle buyers (signed in)
    // or a ROUND2-cookie arrival from the SMS/email link (often signed out).
    // Rescue carts keep their own (stricter) path — flash logic wins over this.
    const second = secondOrderEligible(el) && cart.item_count > 0 && !fs.allFlash && !fs.mixed;
    // Fundraiser exemption — mirrors the checkout rule. Requires something actually in the
    // cart besides the marker: the marker alone is an arrival, not an order.
    const fundraiser = fundraiserCart(cart, el) && cart.item_count > 1 && !fs.allFlash && !fs.mixed;
    const met = subtotal >= minimum || rescue || second || fundraiser;
    const short = Math.max(0, minimum - subtotal);
    const choice = hasChoice(el) || rescue;

    // min progress bar (in the sheet)
    const bar = el.querySelector('[data-spf-min]');
    if (bar) {
      bar.classList.toggle('met', met);
      const fill = bar.querySelector('.fill');
      if (fill) fill.style.width = Math.min(100, (subtotal / minimum) * 100) + '%';
      if (second && short > 0) {
        if (fill) fill.style.width = '100%';
      }
      if (fundraiser && short > 0) {
        if (fill) fill.style.width = '100%';
      }
      const label = bar.querySelector('.label');
      if (label) {
        label.innerHTML =
          fundraiser && short > 0
            ? '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6L9 17l-5-5"/></svg>Fundraiser order — no minimum'
          : second && short > 0
            ? '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6L9 17l-5-5"/></svg>Second order this week — no minimum, fees waived'
          : met
            ? '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6L9 17l-5-5"/></svg>$50 minimum met'
            : `Add <strong class="amt">${money(short)}</strong> more to reach the $50 minimum`;
      }
    }

    // SPF reward-threshold bar (sheet) — same live subtotal as the min gate.
    // Markup + config (threshold/reward/end date) come from the section's
    // Liquid; this only keeps the numbers fresh as candy adds change the cart.
    const rw = el.querySelector('[data-spf-rwbar]');
    if (rw) {
      const rwTh = parseInt(rw.dataset.threshold || '0', 10) * 100; // cents
      const rwEnds = parseInt(rw.dataset.ends || '0', 10) * 1000; // ms epoch, 0 = no end
      // Not on a seed-garlic cart (Mike, 2026-09-15) — same reason as spf-cart-bar.liquid.
      const rwLive = rwTh > 0 && (!rwEnds || Date.now() < rwEnds) && subtotal > 0 && !cartHasSeed();
      rw.hidden = !rwLive;
      if (rwLive) {
        const rwMet = subtotal >= rwTh;
        rw.classList.toggle('met', rwMet);
        const rwFill = rw.querySelector('.rw-fill');
        if (rwFill) rwFill.style.width = Math.min(96, (subtotal / rwTh) * 100) + '%';
        const rwCopy = rw.querySelector('.rw-copy');
        if (rwCopy) {
          rwCopy.innerHTML = rwMet
            ? '<span class="rw-check" aria-hidden="true">✓</span> ' + (rw.dataset.reward || '$10 off') + ' unlocked on this order'
            : `You're <strong>${money(rwTh - subtotal)}</strong> from ${rw.dataset.reward || '$10 off'} this order`;
        }
        const rwTrack = rw.querySelector('.rw-track');
        if (rwTrack) rwTrack.hidden = rwMet;
      }
    }

    // Postal eligibility folds into the gate. Delivery needs a KNOWN in-area
    // code before checkout (required — typed once, remembered after): a
    // confirmed out-of-area code turns Continue into a live "pickup instead"
    // pivot, and an unchecked/partial one asks for the code — with the reason
    // stated right there. Never a mute dead-end: checkout's own "cannot be
    // shipped to the selected address" error is unfixable on our plan, so no
    // uncertain delivery cart gets to see it.
    const postalState = renderPostal(el);
    // Rescue carts never take the delivery path (they ride the existing order),
    // so postal gating is skipped — otherwise a stale Home Delivery attribute
    // could re-create the zero-methods checkout trap for a sub-$50 rescue cart.
    const isDelivery = el.dataset.method === DELIVERY && !rescue;
    const postalIn = postalState === 'in-north' || postalState === 'in-south';
    const postalOut = isDelivery && postalState === 'out';
    const postalNeed = isDelivery && !postalIn && !postalOut;
    const postalCode = (() => {
      const i = el.querySelector('[data-spf-postal]');
      return i ? normPostal(i.value) : '';
    })();

    // sheet footer: normally "Continue to checkout", but under $50 it becomes a
    // live "Keep shopping" action — clearer than a dead, disabled button.
    const cont = el.querySelector('[data-spf-continue]');
    const contText = el.querySelector('[data-spf-continue-text]');
    const help = el.querySelector('[data-spf-help]');
    const contMode =
      fs.mixed ? 'blocked'
      : !met ? 'shop'
      : !choice ? 'blocked'
      : postalOut ? 'postal'
      : postalNeed ? 'postal-need'
      : 'go';
    if (cont) {
      cont.dataset.spfMode = contMode;
      // postal-need stays clickable: on a short screen the postal box sits
      // scrolled out of sight above the foot, so the button takes them to it
      // (2026-10-23, a customer stuck on "Enter your postal code above").
      cont.toggleAttribute('disabled', contMode === 'blocked');
    }
    if (contText) {
      contText.textContent =
        contMode === 'shop' ? (mailValid() ? 'More seed garlic' : 'Keep shopping')
        : contMode === 'blocked' ? 'Choose an option above'
        : contMode === 'postal' ? 'Choose Local Pickup instead'
        : contMode === 'postal-need' ? 'Enter your postal code above'
        : 'Continue to checkout';
    }
    // Sheet footer help line: the $50 nudge while short, then the Shop sign-in hint
    // once they're actually cleared to check out. (Mobile only ever sees the sheet —
    // .cart__ctas is display:none there — so the hint has to live here too.)
    if (help) {
      if (!met) {
        help.dataset.spfTone = 'warn';
        help.innerHTML = `You're ${money(short)} short of the $50 minimum — add a little more`;
      } else if (contMode === 'postal') {
        help.dataset.spfTone = 'warn';
        help.innerHTML = `We don't deliver to <strong>${postalCode}</strong> yet — same fresh order, picked up at any of our 10 spots`;
      } else if (contMode === 'postal-need') {
        help.dataset.spfTone = 'hint';
        help.innerHTML = 'Your postal code confirms we deliver to you — and which day (Wed south / Thu north).';
      } else if (contMode === 'go' && SHOP_SIGNIN_HINT) {
        help.dataset.spfTone = 'hint'; // friendly note, not the red error voice
        help.innerHTML = SHOP_SIGNIN_HINT;
      } else {
        help.removeAttribute('data-spf-tone');
        help.textContent = '';
      }
    }

    // gate Horizon's native checkout button(s) (just blocks reaching checkout)
    document.querySelectorAll('#checkout, button[name="checkout"]').forEach((b) => {
      b.toggleAttribute('disabled', !met || !choice || postalOut || postalNeed || fs.mixed);
    });

    // Desktop: Horizon's checkout button only greys out with no explanation, so
    // surface the short state + a "Keep shopping" link right next to it. Hidden on
    // mobile (where .cart__ctas is display:none and the sticky bar takes over).
    const ctas = document.querySelector('.cart-page .cart__ctas');
    if (ctas) {
      let notice = ctas.querySelector('[data-spf-notice]');
      // Explain the greyed-out checkout for EVERY block reason, not just the $50
      // minimum, and give a one-tap way to resolve it.
      const blockedReason = fs.mixed ? 'mixed' : !met ? 'min' : !choice ? 'choice' : postalOut || postalNeed ? 'postal' : rescue ? 'rescue' : '';
      if (blockedReason) {
        if (!notice) {
          notice = document.createElement('div');
          notice.setAttribute('data-spf-notice', '');
          notice.className = 'spf-cart-notice';
          ctas.appendChild(notice);
        }
        notice.dataset.spfReason = blockedReason;
        if (blockedReason === 'mixed') {
          // Mirrors the Liquid first-paint branch byte-for-byte (keep in sync).
          notice.innerHTML =
            '<span><strong>Rescue deals check out on their own</strong> — they’re packed into the order you already have coming, so the $50 minimum and pickup choice don’t apply to them.</span>' +
            '<button type="button" data-spf-keep="flash">Keep rescue items only →</button>' +
            '<button type="button" data-spf-keep="regular">Keep my regular order only →</button>';
        } else if (blockedReason === 'rescue') {
          notice.innerHTML =
            '<span>🛟 <strong>Tuesday Rescue order</strong> — no minimum, no pickup choice needed. It arrives with the order you already have coming.</span>';
        } else if (blockedReason === 'min') {
          notice.innerHTML =
            `<span>You're <strong>${money(short)}</strong> short of the $50 minimum.</span>` +
            `<span class="spf-cart-notice__track" aria-hidden="true"><span class="spf-cart-notice__fill" style="width:${Math.min(100, (subtotal / minimum) * 100)}%"></span></span>` +
            `<a href="${keepShoppingUrl()}">${mailValid() ? 'More seed garlic →' : 'Keep shopping →'}</a>`;
        } else if (blockedReason === 'postal') {
          notice.innerHTML = postalOut
            ? `<span>We don't deliver to <strong>${postalCode}</strong> yet — pick up instead?</span>` +
              `<button type="button" data-spf-open>See 10 pickup spots →</button>`
            : `<span>Enter your <strong>postal code</strong> so we can confirm your delivery day.</span>` +
              `<button type="button" data-spf-open>Check my postal code →</button>`;
        } else {
          notice.innerHTML =
            `<span>Choose <strong>${mailValid() ? 'pickup, delivery or mail' : 'pickup or delivery'}</strong> to check out.</span>` +
            `<button type="button" data-spf-open>Choose an option →</button>`;
        }
      } else if (SHOP_SIGNIN_HINT) {
        // Nothing blocking them — they're about to hit checkout, which is where the
        // Shop sign-in fork lives. Last moment we own to pre-empt it.
        if (!notice) {
          notice = document.createElement('div');
          notice.setAttribute('data-spf-notice', '');
          notice.className = 'spf-cart-notice';
          ctas.appendChild(notice);
        }
        notice.dataset.spfReason = 'shophint';
        notice.innerHTML = `<span class="spf-cart-notice__hint">${SHOP_SIGNIN_HINT}</span>`;
      } else if (notice) {
        notice.remove();
      }
    }

    // mobile sticky bar
    const barTotal = el.querySelector('[data-spf-bar-total]');
    const barNote = el.querySelector('[data-spf-bar-note]');
    const cta = el.querySelector('[data-spf-cta]');
    const barEl = el.querySelector('[data-spf-bar]');
    if (barEl) barEl.hidden = cart.item_count === 0 || !onCartPage();
    if (barTotal) barTotal.textContent = money(cart.total_price);
    if (barNote) {
      barNote.textContent = fs.mixed
        ? 'Rescue items check out on their own'
        : rescue
        ? 'Tuesday Rescue — no minimum'
        : !met
        ? `Add ${money(short)} to reach the $50 minimum`
        : !choice
        ? 'Pickup or delivery required'
        : postalOut
        ? `No delivery to ${postalCode} — pickup instead`
        : postalNeed
        ? 'Postal code confirms your delivery day'
        : el.dataset.method === MAIL
        ? 'Mail it to me'
        : el.dataset.method === DELIVERY
        ? 'Home Delivery'
        : 'Pickup · ' + (el.dataset.location || '');
    }
    if (cta) {
      cta.removeAttribute('disabled');
      if (fs.mixed) {
        cta.textContent = 'Split the cart';
        cta.dataset.spfAction = 'mixed';
      } else if (rescue) {
        cta.textContent = 'Checkout';
        cta.dataset.spfAction = 'checkout';
      } else if (!met) {
        cta.textContent = mailValid() ? 'More seed garlic' : 'Keep shopping';
        cta.dataset.spfAction = 'shop';
      } else if (!choice || postalOut || postalNeed) {
        cta.textContent = postalOut ? 'Pick a spot' : postalNeed ? 'Add postal code' : 'Choose option';
        cta.dataset.spfAction = 'open';
      } else {
        cta.textContent = 'Checkout';
        cta.dataset.spfAction = 'checkout';
      }
    }
  }

  // ── 1-tap repeat: adopt the remembered pickup spot if nothing's chosen ────
  // Runs on cart-page loads (so the chip shows it) and on sheet open — NOT on
  // every page view site-wide (that would fire a cart write per visit).
  function adoptRemembered(el) {
    // Audit C5: NEVER auto-adopt onto an all-flash cart — the remembered-spot
    // write would stamp NEXT week's pickup attrs on exactly the audience whose
    // items ride THIS week's existing order (repeat buyers, i.e. the whole
    // rescue list). Rescue carts carry no fulfillment attributes at all.
    if (flashState().allFlash) return;
    if (hasChoice(el) || el.dataset.method === DELIVERY) return;
    const r = recallPickup();
    if (!r || !r.name) return;
    el.dataset.method = PICKUP;
    el.dataset.location = r.name;
    el.dataset.locationDay = String(r.day || 3);
    // Prefer the live location button's hours (authoritative if windows change);
    // fall back to the remembered value for older localStorage entries.
    const rBtn = el.querySelector('[data-spf-loc="' + r.name.replace(/"/g, '\\"') + '"]');
    // Only a spot this cart is actually offered. A group's own spot is rendered HIDDEN for
    // carts not tagged for that group (2026-09-23), and a family who picked their school's
    // spot once must not have it re-applied on a later, untagged visit. A remembered spot
    // that no longer exists at all is refused for the same reason.
    if (!rBtn || rBtn.hidden) return;
    el.dataset.locationHours = (rBtn && rBtn.dataset.spfHours) || r.hours || '';
    const dayName = String(r.day) === '4' ? 'Thursday' : 'Wednesday';
    writeAttributes({
      ...CLEAR_CR,
      [ATTR_METHOD]: PICKUP,
      [ATTR_LOCATION]: r.name,
      [ATTR_DAY]: dayName,
      [ATTR_TIME]: el.dataset.locationHours,
      [ATTR_DATE]: fulfillmentDate(parseInt(el.dataset.locationDay, 10)),
    });
  }

  // ── Full sync ─────────────────────────────────────────────────────────────
  async function sync(cartFromEvent) {
    const el = root();
    if (!el) return;
    if (onCartPage()) adoptRemembered(el);
    hydratePostal(el);
    placeChip();
    renderSelection(el);
    renderChip(el);
    const cart = cartFromEvent || (await getCart());
    // Rescue carts carry NO fulfillment attributes: a stale "Home Delivery"
    // method (chosen before the cart went all-flash) would make Shipy hide the
    // pickup rate → zero shipping methods at checkout for a sub-$50 rescue cart
    // (the known trap). Clear once; the write triggers no loop (attributes stay
    // empty afterwards).
    if (onCartPage() && flashState().allFlash && cart && cart.attributes &&
        (cart.attributes[ATTR_METHOD] || cart.attributes[ATTR_LOCATION])) {
      el.dataset.method = '';
      el.dataset.location = '';
      el.dataset.locationDay = '';
      el.dataset.locationHours = '';
      writeAttributes({
        ...CLEAR_CR,
        [ATTR_METHOD]: '', [ATTR_LOCATION]: '', [ATTR_DAY]: '', [ATTR_TIME]: '', [ATTR_DATE]: '',
      });
    }
    // A "Mail" choice left on a cart that gained produce is dead: clear the method so the chip
    // asks again, rather than the cart reaching checkout as Mail with no mail rate. Cart page
    // only, and only when the seed span is actually present (mailValid fails closed without it,
    // and clearing on that basis would wipe a good choice on a page that simply lacks the span).
    // Skipped when adoptRemembered (above, same tick) just chose the shopper's saved pickup
    // spot: its write replaces Mail with Pickup, but the cart read here can predate that write,
    // and clearing the method on top of it strands a pickup location with NO method — caught
    // driving the preview theme 2026-09-15.
    if (onCartPage() && document.querySelector('[data-spf-flash-state]') && !mailValid() &&
        !el.dataset.location &&
        cart && cart.attributes && cart.attributes[ATTR_METHOD] === MAIL) {
      el.dataset.method = '';
      renderSelection(el);
      renderChip(el);
      writeAttributes({ [ATTR_METHOD]: '' });
    }
    // SPF H1: on the cart page, re-stamp a stale pickup date (cart sat across a
    // cutoff) so the native checkout button — which we don't wrap — can't carry last
    // week's date. Only writes when it actually differs (no loop, no needless writes).
    if (onCartPage() && el.dataset.location && el.dataset.method !== DELIVERY && cart && cart.attributes) {
      const day = parseInt(el.dataset.locationDay || '3', 10);
      const fresh = fulfillmentDate(day);
      if (cart.attributes[ATTR_DATE] && cart.attributes[ATTR_DATE] !== fresh) {
        writeAttributes({
          [ATTR_DAY]: String(el.dataset.locationDay) === '4' ? 'Thursday' : 'Wednesday',
          [ATTR_TIME]: el.dataset.locationHours || '',
          [ATTR_DATE]: fresh,
        });
      }
    }
    renderGate(cart, el);
  }

  // ── Delegated interactions ────────────────────────────────────────────────
  document.addEventListener('click', (e) => {
    // open the sheet — from the chip, the cart notice, or the DRAWER CTA (an
    // <a> that falls back to /cart#spf-choose without JS). Opens IN PLACE, no
    // navigation; closes the drawer when it's the origin.
    const opener = e.target.closest('[data-spf-open]');
    if (opener) {
      if (root()) {
        e.preventDefault();
        closeCartDrawer();
        openSheet();
      }
      return;
    }
    if (e.target.closest('[data-spf-close]')) { closeSheet(); return; }

    // candy rack (sheet) / smart recs (cart page + home band) add — same contract.
    const candyAdd = e.target.closest('[data-spf-candy-add]');
    if (candyAdd) {
      if (candyAdd.disabled) return;
      const row = candyAdd.closest('[data-spf-candy-item]');
      const vid = parseInt(candyAdd.dataset.variantId || '', 10);
      if (!vid) return;
      const hrecsWrap = candyAdd.closest('[data-spf-hrecs]');
      const recSurface = candyAdd.closest('[data-spf-strip]') ? 'strip' : hrecsWrap ? (hrecsWrap.dataset.spfHrecsPos || 'home') : candyAdd.closest('[data-spf-candy]') ? 'sheet' : 'cartpage';
      candyAdd.disabled = true;
      candyAdd.textContent = 'Adding…';
      bumpCartIcon(1);
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: vid, quantity: 1 }] }),
      })
        .then((r) => { if (!r.ok) throw new Error('add ' + r.status); return getCart(); })
        .then((cart) => {
          candyAdd.textContent = '✓ Added';
          spfBeacon(recSurface, 'add', vid);
          if (row) row.classList.add('is-added');
          broadcastCart(cart);
          // In the SHEET, collapse the added row after a beat so the next pick
          // surfaces (mobile shows one slot at a time). Cart-page cards stay.
          if (row && row.closest('[data-spf-sheet]')) {
            setTimeout(() => {
              row.classList.add('is-going');
              setTimeout(() => {
                row.remove();
                const el2 = root();
                const box = el2 && el2.querySelector('[data-spf-candy]');
                if (box && !box.querySelector('[data-spf-candy-item]')) {
                  candyLoaded = false;
                  box.hidden = true;
                }
              }, 320);
            }, 900);
          }
        })
        .catch(() => { candyAdd.disabled = false; candyAdd.textContent = '+ Add'; });
      return;
    }

    // Tuesday Rescue mixed-cart split: zero out the OTHER half by VARIANT ID
    // (object-keyed /cart/update.js — never the positional updates[] array that
    // scrambled orders in July). The Liquid + JS notices both render these.
    const keep = e.target.closest('[data-spf-keep]');
    if (keep) {
      const fs = flashState();
      const drop = keep.dataset.spfKeep === 'flash' ? fs.regVariants : fs.flashVariants;
      if (!drop.length) return;
      const updates = {};
      drop.forEach((id) => { updates[id] = 0; });
      keep.disabled = true;
      keep.textContent = 'Updating…';
      fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates }),
      })
        .then((r) => { if (!r.ok) throw new Error('update ' + r.status); return r.json(); })
        .then((cart) => { broadcastCart(cart); sync(cart); })
        .catch(() => { keep.disabled = false; keep.textContent = 'Try again'; });
      return;
    }

    // Second-order customers: Horizon's native checkout buttons must also route
    // through the discount URL, or ROUND2 (and BeSure's exemption) never attaches.
    const nativeCk = e.target.closest('#checkout, button[name="checkout"]');
    if (nativeCk) {
      const sEl = root();
      if (secondOrderRoute(sEl)) {
        e.preventDefault();
        refreshPickupDate(sEl).finally(triggerCheckout);
        return;
      }
      // EVERY route into checkout must re-assert the fulfillment attributes, not just the
      // second-order one. This branch used to do nothing for an ordinary shopper, so the
      // native button carried whatever the cart happened to hold — including nothing, if an
      // app had replaced the attribute set (see refreshPickupDate). A shopper who never
      // touches our own CTA would still land in checkout with no method and no rate.
      //
      // One-shot: the re-click below re-enters this same delegated listener, and without the
      // flag that is an infinite loop. The flag is set BEFORE the await so a double-tap
      // cannot start two.

      if (!nativeCk.dataset.spfReasserted) {
        nativeCk.dataset.spfReasserted = '1';
        e.preventDefault();
        refreshPickupDate(sEl).finally(() => nativeCk.click());
        return;
      }
      // Second pass — fall through and let Shopify's button do its job.
    }

    // mobile bar CTA
    const cta = e.target.closest('[data-spf-cta]');
    if (cta) {
      if (cta.dataset.spfAction === 'open') openSheet();
      else if (cta.dataset.spfAction === 'checkout') refreshPickupDate(root()).finally(triggerCheckout);
      else if (cta.dataset.spfAction === 'shop') window.location.href = keepShoppingUrl();
      else if (cta.dataset.spfAction === 'mixed') {
        // Scroll the split prompt into view — the decision buttons live there.
        const n = document.querySelector('[data-spf-notice][data-spf-reason="mixed"]');
        if (n) n.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    // continue from the sheet (or "keep shopping" when under $50)
    if (e.target.closest('[data-spf-continue]')) {
      const el = root();
      const cont = el && el.querySelector('[data-spf-continue]');
      if (cont && cont.dataset.spfMode === 'shop') { window.location.href = keepShoppingUrl(); return; }
      // Out-of-area delivery: the button IS the pivot — flip to the pickup tab
      // (same handler the toggle uses) instead of dead-ending anybody.
      if (cont && cont.dataset.spfMode === 'postal') {
        const pb = el.querySelector('.toggle [data-spf-method="Pickup"]');
        if (pb) pb.click();
        return;
      }
      if (cont && cont.dataset.spfMode === 'postal-need') {
        focusPostal(el);
        return;
      }
      // Phone is collected at native checkout (required on the address) — no gate
      // here anymore. Re-stamp a FRESH pickup date (so a cart that sat across the
      // Monday cutoff can't carry last week's date), then go. Mode must be 'go' —
      // a disabled button can't click, but belt-and-suspenders against races.
      if (el && hasChoice(el) && cont && cont.dataset.spfMode === 'go') {
        refreshPickupDate(el).finally(triggerCheckout);
      }
      return;
    }

    // method toggle
    const methodBtn = e.target.closest('[data-spf-method]');
    if (methodBtn) {
      const el = root();
      if (!el) return;
      const method = methodBtn.dataset.spfMethod;
      el.dataset.method = method;
      const attrs = { ...CLEAR_CR, [ATTR_METHOD]: method };
      if (method === DELIVERY || method === MAIL) {
        el.dataset.location = '';
        el.dataset.locationDay = '';
        el.dataset.locationHours = '';
        attrs[ATTR_LOCATION] = '';
        attrs[ATTR_DAY] = '';
        attrs[ATTR_TIME] = '';
        attrs[ATTR_DATE] = '';
      }
      renderSelection(el);
      renderChip(el);
      writeAttributes(attrs);
      getCart().then((c) => renderGate(c, el));
      return;
    }

    // pickup location
    const locBtn = e.target.closest('[data-spf-loc]');
    if (locBtn) {
      const el = root();
      if (!el) return;
      el.dataset.method = PICKUP;
      el.dataset.location = locBtn.dataset.spfLoc;
      el.dataset.locationDay = locBtn.dataset.spfDay;
      el.dataset.locationHours = locBtn.dataset.spfHours || '';
      const dayName = locBtn.dataset.spfDay === '4' ? 'Thursday' : 'Wednesday';
      rememberPickup(locBtn.dataset.spfLoc, locBtn.dataset.spfDay, el.dataset.locationHours);
      renderSelection(el);
      renderChip(el);
      writeAttributes({
        ...CLEAR_CR,
        [ATTR_METHOD]: PICKUP,
        [ATTR_LOCATION]: el.dataset.location,
        [ATTR_DAY]: dayName,
        [ATTR_TIME]: el.dataset.locationHours,
        [ATTR_DATE]: fulfillmentDate(parseInt(locBtn.dataset.spfDay || '3', 10)),
      });
      getCart().then((c) => renderGate(c, el));
    }
  });

  // close the sheet on Escape
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      const sheet = root() && root().querySelector('[data-spf-sheet]');
      if (sheet && !sheet.hidden) closeSheet();
    }
  });

  // postal input: verdict repaints instantly; the gate (continue button, native
  // checkout buttons, mobile bar) refreshes on a short debounce so we're not
  // fetching the cart per keystroke.
  let postalTimer = null;
  document.addEventListener('input', (e) => {
    if (!e.target.matches || !e.target.matches('[data-spf-postal]')) return;
    const el = root();
    if (!el) return;
    try { localStorage.setItem(POSTAL_KEY, e.target.value); } catch (err) {}
    renderPostal(el);
    renderConfirm(el);
    clearTimeout(postalTimer);
    postalTimer = setTimeout(() => { getCart().then((c) => renderGate(c, el)); }, 300);
  });

  // re-sync subtotal-driven bits on any cart change (quantity, remove, etc.)
  document.addEventListener('cart:update', (e) => {
    const el = root();
    if (!el) return;
    const cart = e && e.detail && e.detail.resource;
    if (cart && cart.items) {
      placeChip();
      renderSelection(el);
      renderChip(el);
      renderGate(cart, el);
    } else {
      sync();
    }
  });

  // ── Drawer handoff: /cart#spf-choose opens the sheet on arrival ──────────
  // The cart drawer's CTA links here so the shopper lands IN the fulfillment
  // choice (forced when nothing's chosen; a one-glance confirm when a spot is
  // remembered) instead of scrolling a second copy of their cart. The hash is
  // cleared immediately so refresh/back doesn't re-open the sheet.
  function maybeOpenFromHash() {
    if (window.location.hash !== '#spf-choose') return;
    try { history.replaceState(null, '', window.location.pathname + window.location.search); } catch (e) {}
    openSheet();
  }

  // "View full cart" in the sheet is for shoppers NOT already on the cart page.
  function scopeToPage() {
    const el = root();
    if (!el) return;
    const vc = el.querySelector('[data-spf-viewcart]');
    if (vc) vc.hidden = onCartPage();
  }

  // ── Watchdog handshake ────────────────────────────────────────────────────
  // cart-summary.liquid arms a 4s timer that reveals a "text me" fallback unless
  // this flag appears. Set it HERE, after a successful boot — never at the top of
  // the file — so a half-initialised page still counts as a failure. See the long
  // comment on [data-spf-jsfail] in cart-summary.liquid for why this exists.
  function bootDone() { window.__spfReady = true; }

  // Drawer "$50 minimum" notice is server-rendered Liquid, which can't see the
  // ROUND2 cookie — swap its copy client-side for link arrivals (and keep swapping
  // as the drawer re-renders on cart changes). No-op unless the cookie is present.
  function swapDrawerMinForRound2() {
    document.querySelectorAll('.spf-drawer-min:not([data-spf-r2])').forEach((n) => {
      n.setAttribute('data-spf-r2', '');
      n.innerHTML =
        '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 6L9 17l-5-5"/></svg>' +
        '<span><strong>Second order this week</strong> — no minimum, and your fees are waived at checkout.</span>';
    });
  }
  if (hasRound2Cookie()) {
    const armSwap = () => {
      swapDrawerMinForRound2();
      new MutationObserver(swapDrawerMinForRound2).observe(document.body, { childList: true, subtree: true });
    };
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', armSwap);
    else armSwap();
  }

  // ── Back-button / cached page: re-read the cart ───────────────────────────
  // Reported 2026-09-16: press back from checkout and the product page shows an EMPTY basket
  // icon and a quantity stepper that disagrees with the real cart, because the browser served
  // the page it had stored rather than asking Shopify again. The cart itself was always right.
  // A page restored from the back/forward cache re-reads the cart and re-paints from it.
  if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
    window.addEventListener('pageshow', (e) => {
      if (!e || !e.persisted) return;
      getCart().then((cart) => { broadcastCart(cart); sync(cart); }).catch(() => {});
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => { sync(); scopeToPage(); maybeOpenFromHash(); bootDone(); });
  } else {
    sync();
    scopeToPage();
    maybeOpenFromHash();
    bootDone();
  }
})();
